import mixins from 'vue-typed-mixins'
import Dialog from '@/calendesk/models/Dialog'
import { DialogTypes } from '@/components/dialogs/DialogTypes'
import DialogSize from '@/calendesk/models/DialogSize'
import BookingEvent from '@/calendesk/models/BookingEvent'
import FlexibleBooking from '@/calendesk/sections/section/shared/mixins/FlexibleBooking'
import Employee from '@/calendesk/models/DTO/Response/Employee'
import BookingTimeSlot from '@/calendesk/models/BookingTimeSlot'
import createBookingTimeSpots from '@/calendesk/tools/createBookingTimeSpots'
import ServiceSelectable from '@/calendesk/models/ServiceSelectable'
import { DateTimeFormats } from '@/calendesk/models/DateTimeFormats'
import BookingFiltersRequestData from '@/calendesk/models/DTO/Request/BookingFiltersRequestData'
import BookingsResponse from '@/calendesk/models/DTO/Response/BookingsResponse'

export default mixins(FlexibleBooking).extend({
  data: () => ({
    timeSpots: null as Record<string, any> | null,
    events: [] as Array<BookingEvent>,
    selectedTime: null as string | null,
    numberOfDaysToFetchEvents: 10,
    queryDate: null as string | null,
    countAllFreePlaces: false,
    bookingCartSlots: [] as Record<string, string | BookingEvent>[],
    showDatePicker: false,
    preferredEmployeeIdFromTheLastBooking: null as null | number,
    checkedLastBookingForServiceId: null as null | number
  }),
  watch: {
    selectedServiceAndType (newValue: ServiceSelectable | null, oldValue: ServiceSelectable | null) {
      this.checkIfSelectedEmployeeNeedsUpdate()
      if (!newValue || !oldValue ||
        (newValue && oldValue && JSON.stringify(newValue) !== JSON.stringify(oldValue))) {
        this.isFetchingTimeSlots = true
        this.checkIfCanResetDate()
        this.reloadEventsDebounce()
      }
    },
    selectedDate (newDate: string, oldDate: string) {
      if (newDate !== oldDate && this.numberOfHoursForDate(newDate) === 0) {
        this.isFetchingTimeSlots = true
        this.reloadEventsDebounce()
      }
    },
    selectedEmployeeId (newEmployeeId: number | null, oldEmployeeId: number | null) {
      if (newEmployeeId !== oldEmployeeId) {
        this.isFetchingTimeSlots = true
        this.checkIfCanResetDate()
        this.reloadEventsDebounce()
      }
    },
    shouldReload (reload) {
      if (reload) {
        this.isFetchingTimeSlots = true
        this.reloadEventsDebounce()
        this.setReloadAllBookings(false)
        this.bookingCartSlots = []
      }
    },
    customerTimeZone (newTz: string | null, oldTz: string | null) {
      if (newTz !== oldTz) {
        this.isFetchingTimeSlots = true
        this.reloadEventsDebounce()
      }
    }
  },
  mounted () {
    this.setupCalendar()
  },
  computed: {
    isUsingCartWithServiceMaxOnePeople (): boolean {
      return this.data.configuration.wb_use_booking_cart__checkbox__ && this.serviceMaxPeople === 1
    },
    canUseCartStepper (): boolean {
      return this.data.configuration.wb_use_booking_cart__checkbox__ && this.serviceMaxPeople > 1
    },
    showServiceColumn (): boolean {
      return (typeof this.data.configuration.wb_show_service_column__checkbox__ === 'undefined' ||
        !!this.data.configuration.wb_show_service_column__checkbox__)
    },
    mergedTimeSpots (): Array<BookingTimeSlot> {
      let spots: any = []

      if (this.timeSpots) {
        if (this.selectedEmployeeId) {
          if (this.timeSpots[this.selectedEmployeeId] && this.timeSpots[this.selectedEmployeeId][this.selectedDate as string]) {
            const dateValues = this.timeSpots[this.selectedEmployeeId][this.selectedDate as string]
            if (dateValues && dateValues.length > 0) {
              spots = [...spots, ...dateValues]
            }
          }
        } else {
          for (const key of Object.keys(this.timeSpots as {})) {
            const dateValues = this.timeSpots[key][this.selectedDate as string]
            if (dateValues && dateValues.length > 0) {
              spots = [...spots, ...dateValues]
            }
          }
        }

        return createBookingTimeSpots(spots, this.serviceMaxPeople)
      }

      return []
    },
    firstAvailableDate (): string | null {
      const dates: Set<string> = new Set()
      if (this.timeSpots) {
        if (this.selectedEmployeeId && this.timeSpots[this.selectedEmployeeId]) {
          for (const key of Object.keys(this.timeSpots[this.selectedEmployeeId] as {})) {
            const hours = this.timeSpots[this.selectedEmployeeId][key]
            if (hours && hours.length > 0) {
              dates.add(key)
            }
          }
        } else {
          for (const employeeId of Object.keys(this.timeSpots as {})) {
            for (const key of Object.keys(this.timeSpots[employeeId] as {})) {
              const hours = this.timeSpots[employeeId][key]
              if (hours && hours.length > 0) {
                dates.add(key)
              }
            }
          }
        }
      }

      const result = [...dates].sort(function (a, b) {
        return new Date(a).getTime() - new Date(b).getTime()
      })

      if (result.length > 0) {
        return result[0]
      }

      return null
    }
  },
  methods: {
    setDate (date: string) {
      this.selectedDate = date
      this.showDatePicker = false

      if (this.numberOfHoursForDate(date) === 0) {
        this.isFetchingTimeSlots = true
        this.reloadEventsDebounce()
      }
    },
    checkIfSelectedEmployeeNeedsUpdate () {
      if (this.serviceEmployees.length === 1 && this.selectedEmployeeId !== this.serviceEmployees[0].id) {
        this.selectedEmployeeId = this.serviceEmployees[0].id
      } else if (this.selectedEmployeeId) {
        const availableEmployees = this.serviceEmployees.map((e) => e.id)
        if (!availableEmployees.includes(this.selectedEmployeeId)) {
          this.selectedEmployeeId = null
        }
      }
    },
    checkIfCanResetDate () {
      if (!this.lockDateChange) {
        const date = this.requestedDate ?? this.$moment().format(DateTimeFormats.FULL)
        this.setDate(date)
      }
    },
    countSpots (dateValues: any[]) {
      let result = 0

      if (this.countAllFreePlaces &&
        this.selectedServiceAndType &&
        this.selectedServiceAndType.service.maxPeople > 1) {
        const allSpots = dateValues.length * this.selectedServiceAndType.service.maxPeople
        let usedSpots = 0
        dateValues.forEach((spot: Record<string, any>) => {
          usedSpots += spot.used
        })
        result = allSpots - usedSpots
      } else {
        result = dateValues.length
      }

      return result
    },
    numberOfHoursForDate (date: string): number {
      let result = 0

      if (this.timeSpots) {
        if (this.selectedEmployeeId) {
          if (this.timeSpots[this.selectedEmployeeId] && this.timeSpots[this.selectedEmployeeId][date]) {
            const dateValues = this.timeSpots[this.selectedEmployeeId][date]
            if (dateValues && dateValues.length > 0) {
              result = this.countSpots(dateValues)
            }
          }
        } else {
          for (const key of Object.keys(this.timeSpots as {})) {
            const dateValues = this.timeSpots[key][date]
            if (dateValues && dateValues.length > 0) {
              result += this.countSpots(dateValues)
            }
          }
        }
      }

      return result
    },
    reloadEventsDebounce () {
      this.debounce(this.reloadEvents, 400)()
    },
    getEventsStartDate (): string {
      let startDate = this.queryDate ? this.queryDate : this.selectedDate
      if (this.lockDateChange) {
        const requestedDate = this.$moment(startDate)
        const minDate = this.$moment(this.minDate)
        const maxDate = this.maxDate ? this.$moment(this.maxDate) : null

        if (requestedDate.isBefore(minDate)) {
          startDate = minDate.format(DateTimeFormats.FULL)
        } else if (maxDate && requestedDate.isAfter(maxDate)) {
          startDate = minDate.format(DateTimeFormats.FULL)
        }
      }
      return startDate
    },
    getEventsNumberOfDays (): number {
      let numberOfDays = this.numberOfDaysToFetchEvents || 10

      if (this.lockDateChange && this.minDate && this.maxDate) {
        const minDate = this.$moment(this.minDate)
        const maxDate = this.$moment(this.maxDate)
        const startDate = this.$moment(this.getEventsStartDate())

        numberOfDays = maxDate.diff(minDate, 'days') + 1
        const maxNumberOfDays = maxDate.diff(startDate, 'days') + 1

        if (numberOfDays > maxNumberOfDays) {
          numberOfDays = maxNumberOfDays
        }
      }

      return numberOfDays
    },
    reloadEvents () {
      if (this.selectedServiceAndType && this.selectedDate) {
        this.isFetchingTimeSlots = true

        this.fetchAvailableBookingSlots({
          used_slots: 1,
          number_of_days: this.getEventsNumberOfDays(),
          service_id: this.selectedServiceAndType.service.id,
          employee_ids: this.serviceEmployees.map((employee) => employee.id).join(','),
          start_date: this.getEventsStartDate(),
          service_type_id: this.selectedServiceAndType.serviceType?.id,
          customer_time_zone: this.customerTimeZone
        }).then((response: Record<string, any>) => {
          this.timeSpots = response
        }).catch((error) => {
          this.$log.info('fetchAvailableBookingSlots@catch', error)
        }).finally(() => {
          this.isFetchingTimeSlots = false

          if (!this.firstDateSet && this.mergedTimeSpots.length === 0 && this.firstAvailableDate) {
            this.firstDateSet = true
            this.selectedDate = this.firstAvailableDate
          }

          this.reloadPreferredEmployee()
        })
      }
    },
    bookSelectedTime (slot: BookingTimeSlot) {
      if (this.selectedServiceAndType) {
        this.userRequestedEmployee = !!this.selectedEmployeeId
        const employee = this.getSelectedEmployeeForTime(slot)

        if (employee) {
          const event = new BookingEvent(
            this.selectedServiceAndType.service,
            employee,
            this.selectedServiceAndType.serviceType,
            this.selectedDate,
            slot.time as string,
            null,
            null,
            null,
            this.customerTimeZone,
            this.userRequestedEmployee)

          this.openDialog(new Dialog(
            true,
            DialogTypes.BOOKING_FORM_DIALOG,
            null,
            DialogSize.MIDDLE,
            false,
            'center',
            {
              events: [event],
              configurationData: this.data
            },
            true))
        }
      }
    },
    getSelectedEmployeeForTime (slot: BookingTimeSlot): Employee | null {
      if (this.timeSpots) {
        const availableEmployeeIds: Set<number> = new Set()

        for (const [key, dates] of Object.entries(this.timeSpots)) {
          const employeeDates = dates[this.selectedDate]
          if (employeeDates) {
            const isSlotAvailable = employeeDates.some((item: BookingTimeSlot) => item.time === slot.time)
            if (isSlotAvailable) {
              availableEmployeeIds.add(parseInt(key, 10))
            }
          }
        }

        if (this.selectedEmployeeId && availableEmployeeIds.has(this.selectedEmployeeId)) {
          const employee = this.serviceEmployees.find((employee: Employee) => employee.id === this.selectedEmployeeId)

          if (employee) {
            return employee
          }
        } else {
          let employee = undefined as Employee | undefined

          if (this.preferredEmployeeIdFromTheLastBooking &&
            availableEmployeeIds.has(this.preferredEmployeeIdFromTheLastBooking)) {
            employee = this.serviceEmployees.find((employee: Employee) => employee.id === this.preferredEmployeeIdFromTheLastBooking)
          }

          if (!employee) {
            const randomEmployeeId = this.getRandomItemFromSet(availableEmployeeIds)
            employee = this.serviceEmployees.find((employee: Employee) => employee.id === parseInt(randomEmployeeId))
          }

          if (employee) {
            return employee
          }
        }
      }

      return null
    },
    reloadPreferredEmployee () {
      if (this.isUserLogged &&
        this.selectedServiceAndType &&
        this.selectedServiceAndType.service &&
        this.checkedLastBookingForServiceId !== this.selectedServiceAndType.service.id) {
        this.checkedLastBookingForServiceId = this.selectedServiceAndType.service.id

        const data = new BookingFiltersRequestData()
        data.limit = 1
        data.ascending = 0
        data.order_by = 'id'
        data.service_ids = `${this.selectedServiceAndType.service.id}`

        this.fetchBookings(data).then((bookings: BookingsResponse) => {
          if (bookings.data && bookings.data.length > 0 && bookings.data[0].employee) {
            this.preferredEmployeeIdFromTheLastBooking = bookings.data[0].employee.id
          }
        })
      }
    },
    setupCalendar () {
      if (!this.calendarInitialServiceSelectionSupported) {
        this.selectFirstServiceAsDefault()
        this.checkIfSelectedEmployeeNeedsUpdate()

        this.$nextTick(() => {
          this.reloadEventsDebounce()
        })
      }
    }
  }
})
